import React, { useEffect, useState } from 'react';
import Markdown from 'markdown-to-jsx';
import Breadcrumb from './Breadcrumb';
import fixImagePaths from '../utils/fixImagePaths';
import ProductDocSectionHeader from './ProductDocSectionHeader';

export default function ProductDocPage({
  breadcrumbItems,
  markdownPath,
  pageTitle,
  sectionHeader,
  contentTitle,
  contentBoxStyle,
  children,
}) {
  const [markdown, setMarkdown] = useState('');

  useEffect(() => {
    document.title = pageTitle;
    fetch(markdownPath)
      .then(res => res.text())
      .then(md => setMarkdown(fixImagePaths(md)));
  }, [markdownPath, pageTitle]);

  return (
    <div className="products-doc-bg" style={{ paddingBottom: '3rem' }}>
      <div className="products-doc-page">
        <Breadcrumb items={breadcrumbItems} />
        <section className="section section--light" style={{ marginTop: 0, paddingBottom: 0 }}>
          <div className="container" style={{ maxWidth: 800, margin: '0 auto' }}>
            <ProductDocSectionHeader {...sectionHeader} />
          </div>
        </section>
        <div
          style={{
            border: '1.5px solid #e2e8f0',
            background: '#fcfcfd',
            borderRadius: 18,
            padding: '2.2rem 2rem',
            margin: '32px auto 3rem auto',
            maxWidth: 936,
            boxSizing: 'border-box',
            boxShadow: '0 2px 12px rgba(44,82,130,0.04)',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            ...contentBoxStyle,
          }}
        >
          {contentTitle && (
            <h1
              className="section__title"
              style={{
                fontSize: '2.1rem',
                marginBottom: '2rem',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                textAlign: 'left',
              }}
            >
              {contentTitle}
            </h1>
          )}
          <Markdown
            options={{
              forceBlock: true,
              overrides: {
                img: {
                  component: props => (
                    <img {...props} style={{ maxWidth: '100%' }} alt={props.alt || ''} />
                  ),
                },
                a: {
                  component: props => {
                    const href = props.href || '';
                    const isExternal =
                      href.startsWith('http://') ||
                      href.startsWith('https://') ||
                      href.startsWith('mailto:');
                    const isHash = href.startsWith('#');
                    if (isExternal) {
                      return (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                          {props.children}
                        </a>
                      );
                    }
                    // For hash links and relative links, open in same tab
                    return <a {...props}>{props.children}</a>;
                  },
                },
                div: {
                  component: props => <div {...props}>{props.children}</div>,
                },
                p: {
                  component: props => (
                    <p {...props} style={{ margin: 0 }}>
                      {props.children}
                    </p>
                  ),
                },
                h1: {
                  component: props => (
                    <h1
                      {...props}
                      style={{
                        fontSize: '2.1rem',
                        fontWeight: 700,
                        marginTop: '2.2rem',
                        marginBottom: '1.1rem',
                        letterSpacing: '-0.5px',
                        lineHeight: 1.15,
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </h1>
                  ),
                },
                h2: {
                  component: props => (
                    <h2
                      {...props}
                      style={{
                        fontSize: '1.45rem',
                        fontWeight: 700,
                        marginTop: '1.8rem',
                        marginBottom: '0.9rem',
                        letterSpacing: '-0.5px',
                        lineHeight: 1.18,
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </h2>
                  ),
                },
                h3: {
                  component: props => (
                    <h3
                      {...props}
                      style={{
                        fontSize: '1.18rem',
                        fontWeight: 600,
                        marginTop: '1.4rem',
                        marginBottom: '0.7rem',
                        letterSpacing: '-0.3px',
                        lineHeight: 1.22,
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </h3>
                  ),
                },
                h4: {
                  component: props => (
                    <h4
                      {...props}
                      style={{
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        marginTop: '1.1rem',
                        marginBottom: '0.6rem',
                        letterSpacing: '-0.2px',
                        lineHeight: 1.22,
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </h4>
                  ),
                },
                h5: {
                  component: props => (
                    <h5
                      {...props}
                      style={{
                        fontSize: '0.98rem',
                        fontWeight: 500,
                        marginTop: '0.9rem',
                        marginBottom: '0.5rem',
                        letterSpacing: '-0.1px',
                        lineHeight: 1.22,
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </h5>
                  ),
                },
                h6: {
                  component: props => (
                    <h6
                      {...props}
                      style={{
                        fontSize: '0.92rem',
                        fontWeight: 500,
                        marginTop: '0.7rem',
                        marginBottom: '0.4rem',
                        letterSpacing: '-0.1px',
                        lineHeight: 1.22,
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </h6>
                  ),
                },
                table: {
                  component: props => (
                    <table
                      {...props}
                      style={{
                        borderCollapse: 'collapse',
                        width: '100%',
                        border: '1px solid #e2e8f0',
                        margin: '1.5rem 0',
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </table>
                  ),
                },
                th: {
                  component: props => (
                    <th
                      {...props}
                      style={{
                        border: '1px solid #e2e8f0',
                        padding: '0.5rem 0.75rem',
                        background: '#f8fafc',
                        fontWeight: 600,
                        textAlign: 'left',
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </th>
                  ),
                },
                td: {
                  component: props => (
                    <td
                      {...props}
                      style={{
                        border: '1px solid #e2e8f0',
                        padding: '0.5rem 0.75rem',
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </td>
                  ),
                },
                code: {
                  component: props => (
                    <code
                      {...props}
                      style={{
                        fontFamily:
                          'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        fontSize: '0.98em',
                        background: '#f4f4f7',
                        borderRadius: 4,
                        padding: '0.1em 0.4em',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: props.className ? 'pre-wrap' : undefined,
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </code>
                  ),
                },
                pre: {
                  component: props => (
                    <pre
                      {...props}
                      style={{
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        margin: '1.1em 0',
                        background: '#f4f4f7',
                        borderRadius: 4,
                        padding: '0.7em 1em',
                        fontSize: '0.98em',
                        ...props.style,
                      }}
                    >
                      {props.children}
                    </pre>
                  ),
                },
              },
            }}
          >
            {markdown}
          </Markdown>
          {children}
        </div>
      </div>
    </div>
  );
}
